
/// <reference types="resize-observer-browser" />
// @ts-ignore
import iframeCss from "./OverlaidUI/styles.iframecss";
// @ts-ignore
import iframeSingularityCss from "./OverlaidUI/singularity-styles.iframecss";

import Vue from "vue";
import { Component } from "vue-property-decorator";

import Canvas from "./Canvas.vue";
import store, {
  configStore,
  documentStore,
  globalStore,
  uiStore
} from "@/store/store";
import { initializeOrReinitializeCombokeys } from "@/lib/keyboard-shortcuts/keyboard-shortcuts";
import PreviewWidthAdjustmentHandle from "../Reusable/Iframe/PreviewWidthAdjustmentHandle.vue";
import { getMinimumWidth } from "@/lib/breakpoints/breakpoints";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import StandardButton from "@/components/UI/StandardButton.vue";
import vuetify from "@/plugins/vuetify";
import ForeignDocumentsOverlay from "@/components/Canvas/ForeignDocumentsOverlay.vue";
import { createStyleTagWithRawCssCodeInside } from "@/util/dom";
import {
  getBuilderCanvasHead,
  setWindowBreakdanceCanvasDocument
} from "@/lib/canvas/helpers";
import Loading from "@/components/Reusable/Loading.vue";
import IframeWrapper from "@/components/Reusable/Iframe/IframeWrapper.vue";
import { CanvasPreviewWidth } from "@/types/ui";

@Component({
  components: {
    IframeWrapper,
    Loading,
    Canvas,
    ForeignDocumentsOverlay,
    PreviewWidthAdjustmentHandle,
    StandardButton
  }
})
export default class IframeCanvas extends Vue {
  iframeExited = false;
  foreignDocumentsOverlayVueApp: Vue | null = null;
  canvasApp: Vue | null = null;

  handlePreviewWidthChange(previewWidth: CanvasPreviewWidth) {
    uiStore.changePreviewWidthMutation(previewWidth);
  }

  handlePreviewWidthChangeWithAdjustmentBar(previewWidthInPx: number) {
    uiStore.changePreviewWidthWithAdjustmentBar(previewWidthInPx);
  }

  handleResetPreviewWidth() {
    uiStore.selectBaseBreakpoint();
  }

  setIframeAsExited() {
    if (configStore.leavingIframeOnPurpose) {
      configStore.setLeavingIframeOnPurpose(false);
    } else {
      this.iframeExited = true;
    }
  }

  reloadIframe() {
    this.iframeExited = false;
    // the reload needs to be timeouted to wait for the iframe block to be rendered
    setTimeout(() => {
      // @ts-ignore
      window.frames["iframe"].location.reload();
    }, 0);
  }

  get iframeMinWidth() {
    return getMinimumWidth(globalStore);
  }

  get canvasPreviewWidth() {
    return uiStore.canvasPreviewWidth;
  }

  get canvasState() {
    return uiStore.canvasState;
  }

  get currentBreakpoint() {
    return uiStore.currentBreakpoint;
  }

  get iframeURL() {
    if (
      configStore.documentSpecificNeededUrls &&
      documentStore.document.documentMeta.id !== -1
    ) {
      const iframeUrl = new URL(
        configStore.documentSpecificNeededUrls.iframeUrl
      );
      iframeUrl.searchParams.append("breakdance_iframe", "true");
      iframeUrl.searchParams.append(
        "breakdance_open_document",
        `${documentStore.document.documentMeta.id}`
      );
      return iframeUrl.toString();
    }
    return null;
  }

  protected mountForeignDocumentsOverlayVueAppInIframe(
    iframeDocument: Document,
    canvasEl: Element
  ) {
    const overlayMountpoint = iframeDocument.createElement("div");
    overlayMountpoint.classList.add(
      "breakdance-foreign-documents-overlay-mountpoint"
    );
    iframeDocument.body.appendChild(overlayMountpoint);

    this.foreignDocumentsOverlayVueApp = new Vue<{
      fragment: DocumentFragment | null;
      builderUrl: string | null;
    }>({
      el: overlayMountpoint,
      store,
      vuetify,
      render(h) {
        return h(ForeignDocumentsOverlay, {
          props: {
            elementsToWatch: iframeDocument.querySelectorAll(
              "[data-breakdance-foreign-document]"
            ),
            canvasElement: canvasEl,
            iframeDocument
          },
          on: {
            openDocument: (docInfo: { builderUrl: string; title: string }) => {
              if (confirm(`Edit ${docInfo.title}?`)) {
                window.open(docInfo.builderUrl, "_top");
              }
            }
          }
        });
      }
    });
  }

  mountCanvasInIframe(loadedIframe: HTMLIFrameElement) {
    if (!loadedIframe.contentDocument) {
      throw "Unable to mount canvas in iframe because its document is missing";
    }

    setWindowBreakdanceCanvasDocument(loadedIframe.contentDocument);

    initializeOrReinitializeCombokeys(
      document,
      window.Breakdance.canvas.document
    );

    breakdanceStoreActions.registerAllStoreActions();

    const canvasVueInstanceMountpoint =
      window.Breakdance.canvas.document.querySelector(
        "#breakdance-canvas-wrapper"
      ) as Element;

    this.canvasApp = new Vue({
      store,
      vuetify,
      render: h => h(Canvas)
    }).$mount(canvasVueInstanceMountpoint);

    this.mountForeignDocumentsOverlayVueAppInIframe(
      window.Breakdance.canvas.document,
      this.canvasApp.$el
    );

    getBuilderCanvasHead().appendChild(
      createStyleTagWithRawCssCodeInside(document, iframeCss, "iframe-styles")
    );

    getBuilderCanvasHead().appendChild(
      createStyleTagWithRawCssCodeInside(document, iframeSingularityCss, "iframe-singularity-styles")
    );

    this.unbindClickEventOnInnerIframeLinks();
    this.$emit("loaded");
  }

  unbindClickEventOnInnerIframeLinks() {
    const allLinks = [
      ...window.Breakdance.canvas.document.querySelectorAll(
        "a:not([force-allow-clicks])"
      )
    ];
    const linksInsideBreakdanceCanvas = [
      ...window.Breakdance.canvas.document.querySelectorAll(
        "#breakdance_canvas a"
      )
    ];

    // We don't want to alter BuilderElement(s) behavior
    const linksExcludingTheOnesInBreakdanceCanvas = allLinks.filter(
      element => !linksInsideBreakdanceCanvas.includes(element)
    );

    linksExcludingTheOnesInBreakdanceCanvas.forEach(link => {
      link.addEventListener("click", (event: Event) => {
        event.preventDefault();
      });
    });
  }

  destroyed() {
    if (this.canvasApp) {
      this.canvasApp.$destroy();
    }

    if (this.foreignDocumentsOverlayVueApp) {
      this.foreignDocumentsOverlayVueApp.$destroy();
    }
  }
}
